import { useRouter } from 'next/router';
import { styled } from '@mui/material/styles';
// @mui
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
// components
import Iconify from '../../../components/Iconify';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// utils
import { fKrKSTMatchingCard, fKrKSTMatchingCardMobile } from '../../../utils/formatTime';
// routes
import { PATH_SETTINGS, PATH_OFFER } from '../../../routes/paths';

// ----------------------------------------------------------------------
export default function MatchingCard({ offers = [], isLoading }) {
  const router = useRouter();

  if (isLoading || !offers) return <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }}/>
  if (offers?.length > 0) {
    return (
      <Card>
        <Stack divider={<Divider sx={{ mx: 2 }} />}>
        {offers?.map((data, index) => (
          <MatchinRow key={index} data={data} />
        ))}
        </Stack>
      </Card>
    )
  } else {
    return (
      <Card sx={{ px: 2 }}>
        <Stack sx={{ py: 5 }} justifyContent={"center"} alignItems="center">
          <Typography variant="subtitle1" sx={{ color: "text.secondary", textAlign: "center" }} gutterBottom>
            아직 받은 매칭이 없어요.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }} paragraph>
            매칭을 더 빨리 받을 수 있도록 프로필을 수정해보세요!
          </Typography>
          <Button
            variant="contained"
            onClick={() => router.push(PATH_SETTINGS.root)}
            sx={{ width: "fit-content" }}
          >
            프로필 수정하기
          </Button>
        </Stack>
      </Card>
    )
  }
}

function MatchinRow({ data }) {
  if (!data) return <Skeleton variant="rectangular" height={80}/>

  const goOffer = (oid) => {
    window.open(`${PATH_OFFER.root}?oid=${oid}`, '_blank')
  }
  const smUp = useResponsive("up", "sm");
  const running = [
    "sent", "modify-sent", "auto-preprocessing", "offer-to-company", "other-3way-time-requested", "reselect-3way-time", 
    "waiting-3way-meeting", "confirm-3way-meeting", "waiting-offer-from-crew", 
    "crew-negotiate-to-company", "company-negotiate-rewrite"
  ].includes(data?.status);
  const hastodo = [
    "sent", "modify-sent", "other-3way-time-requested", "waiting-3way-meeting", 
    "waiting-offer-from-crew", "company-negotiate-rewrite"
  ].includes(data?.status);

  const buttonTextMap = {
    "sent": "매칭된 회사를 확인해주세요.",
    "modify-sent": "조건을 수정해주세요.",
    "auto-preprocessing": "회사가 검토하고 있어요. 조금만 기다려주세요.",
    "offer-waitlisted": "대기열 등록됨",
    "offer-to-company": "회사가 검토하고 있어요. 조금만 기다려주세요.",
    "other-3way-time-requested": "다른 미팅 일정을 선택해주세요.",
    "reselect-3way-time": "회사가 검토하고 있어요.",
    "waiting-3way-meeting": "미팅 일정을 확인해주세요.",
    "confirm-3way-meeting": "미팅 일정이 확정됐어요.",
    "waiting-offer-from-crew": "계약 조건을 제안해주세요.",
    "crew-negotiate-to-company": "회사가 계약을 검토하고 있어요. 조금만 기다려주세요.",
    "company-negotiate-rewrite": "계약 조건을 수정해주세요.",
    "company-reject": "회사가 제안을 거절했습니다.",
    "crew-reject": "제안을 거절했습니다.",
    "crew-no-response": "매칭이 취소되었습니다.",
    "accept": "계약 완료!",
    "company-stop": "회사가 공고를 중단했습니다.",
    "company-3way-no-response": "회사가 응답이 없어 매칭이 취소되었습니다.",
    "company-contract-no-response": "회사가 응답이 없어 매칭이 취소되었습니다.",
    "crew-contract-no-response": "크루가 응답이 없어 매칭이 취소되었습니다.",
    "archive": "회사가 공고를 중단했습니다.",
    "closed-excluded": "공고가 마감되었습니다.",
  };
  const buttonText = buttonTextMap[data?.status] || "확인하기";

  return (
    <Box
      sx={{
        width: 1, height: "80px", px: 2,
        "&:hover": {
          bgcolor: "grey.100",
          cursor: "pointer",
        },
        "&:active": {
          bgcolor: "grey.200",
        }
      }}
      onClick={() => goOffer(data.uuid)}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: 1 }} spacing={1}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          {running ? <GreenCircle /> : <GreyCircle />}
          <Stack spacing={1}>
            <Stack direction={smUp ? "column" : "row"} spacing={smUp ? 0 : 1} alignItems={smUp ? "flex-start" : "center"}>
              <Typography variant="subtitle2" noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: smUp ? 400 : 160 }}>
                {data?.company}
              </Typography>
              <Typography variant={smUp ? "body2" : "caption"} sx={{ color: "text.secondary", minWidth: "auto" }} noWrap>
                {smUp ? fKrKSTMatchingCard(data?.sent_at, data?.crew_applied_at) : fKrKSTMatchingCardMobile(data?.sent_at, data?.crew_applied_at)}
              </Typography>
            </Stack>
            {!smUp && (
              <Typography variant="overline"
                sx={{
                  color: hastodo ? "text.primary" : "text.disabled",
                  fontWeight: (running && !hastodo) ? "normal" : "bold",
                }}
              >
                {buttonText}
              </Typography>
            )}
          </Stack>
        </Stack>
        {smUp ? (
          <Button
            variant="text"
            size="small"
            endIcon={<Iconify icon="eva:arrow-ios-forward-fill" sx={{ color: hastodo ? "text.primary" : "text.disabled" }} />}
            edge="end"
            sx={{
              color: hastodo ? "text.primary" : "text.disabled",
              fontWeight: (running && !hastodo) ? "normal" : "bold",
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: "auto",
            }}
          >
            {buttonText}
          </Button>
        ) : (
          <IconButton edge="end" sx={{ color: hastodo ? "text.primary" : "text.disabled" }}>
            <Iconify icon="eva:arrow-ios-forward-fill" />
          </IconButton>
        )}
      </Stack>
    </Box>
  )
};

const GreenCircle = styled('span')(({ theme }) => ({
  height: 10,
  width: 10,
  marginRight: "4px",
  backgroundColor: theme.palette.oldprimary.main,
  borderRadius: '50%',
  display: 'inline-block',
}));

const GreyCircle = styled('span')(({ theme }) => ({
  height: 10,
  width: 10,
  marginRight: "4px",
  backgroundColor: theme.palette.grey[300],
  borderRadius: '50%',
  display: 'inline-block',
}));
